import React, { useMemo, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HeadCellType, TableContextProvider } from '../../../interfaces/common';
import { buildHeadCells } from '../../../utils/methods';
import { VALIDATION_FIELD_MAPPING, HEAD_CELL_SORT_MAPPING } from '../../../utils/appConstant';
import useSorting from '../../../hooks/useSorting';
import usePagination from '../../../hooks/usePagination';
import TableBox from '../../../components/TableBox';
import { SortOrder, RoleCodes } from '../../../enums';
import useFetch from '../../../hooks/useFetch';
import { AuthContext } from '../../../contexts/AuthContext';
import UserService from '../../../services/user.service';
import PropertyService from '../../../services/property.service';
import TopSection from './components/TopSection';
import BasicDetails from './components/BasicDetails';
import TableHeaderSection from './components/TableHeaderSection';
import Row from './components/Row';

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const { loggedInUser } = useContext(AuthContext);
  const isInspector = loggedInUser?.role_code === RoleCodes.inspector;

  const [profileDetails, setProfileDetails] = useState<any>({})

  const headCells: HeadCellType[] = buildHeadCells([
    VALIDATION_FIELD_MAPPING.image,
    VALIDATION_FIELD_MAPPING.name,
    VALIDATION_FIELD_MAPPING.city,
    VALIDATION_FIELD_MAPPING.managedBy,
  ], 'userProfileProperties');

  const {
    order, orderBy, setOrder, setOrderBy,
  } = useSorting({ defaultOrder: SortOrder.asc, defaultOrderBy: VALIDATION_FIELD_MAPPING.name });
  const {
    page, perPage, setPage, setPerPage,
  } = usePagination({ defaultPage: 1, defaultPerPage: 5 });

  useFetch({
    service: UserService,
    func: 'get',
    payload: loggedInUser.id,
    dependency: [loggedInUser.id],
    skip: !loggedInUser.id,
    onSuccess: async (response) => {
      setProfileDetails(response || {})
    },
    onError: (e: any) => {
      setProfileDetails({})
    },
  });

  const { total, data: properties, loading } = useFetch({
    service: PropertyService,
    func: 'list',
    payload: {
      page, perPage,
      orderBy: HEAD_CELL_SORT_MAPPING.userProfileProperties[orderBy] || orderBy,
      order,
      status: 1,
      myProperties: 1,
    },
    dependency: [order, orderBy, page, perPage, isInspector],
    skip: isInspector,
  });

  const headerSection = useMemo(() => <TableHeaderSection total={total} />, [total]);

  const values: TableContextProvider = useMemo(() => {
    return {
      headerSection,
      row: Row,
      data: properties || [],
      pagination: true,
      headCells,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      noDataFoundMessage: t("users.profile.pageTexts.properties.table.noDataFoundMessage"),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy
    };
  }, [headCells, order, orderBy, page, perPage, total, loading, properties, setPage, setPerPage, setOrder, setOrderBy]);

  return (
    <div className='flex-grow-1'>
      <div className='row'>
        <div className='col-12'>
          <TopSection
            email={profileDetails.email}
            fullName={profileDetails.full_name}
            roles={profileDetails.roles}
            manager={profileDetails.manager}
          />
        </div>
      </div>

      <div className='row'>
        <div className={isInspector ? 'col-12 mb-6' : 'col-xl-4 col-lg-5 col-md-5 mb-6'}>
          <BasicDetails
            email={profileDetails.email}
            firstName={profileDetails.first_name}
            lastName={profileDetails.last_name}
            active={profileDetails.is_active}
            countryCode={profileDetails.country_code}
            phoneNumber={profileDetails.phone_number}
            createdAt={profileDetails.created_at}
          />
        </div>

        {!isInspector && (
          <div className='col-xl-8 col-lg-7 col-md-7'>
            <TableBox
              tableValues={values}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;